import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import Login from "../Login/component";
import BankTransferPaymentDialog, {
  BankTransferPaymentDetails,
} from "./BankTransferPaymentDialog";
import {
  bookOneOffSession,
  fetchSharedOneOffSession,
} from "./api";
import type { SharedOneOffSessionInfo } from "./types";
import { buildCompactInvoiceReference } from "./invoicePaymentUtils";
import ConfirmSessionBookingDialog from "./ConfirmSessionBookingDialog";

const MAX_PLACES_NO_CAPACITY = 20;

function maxBookablePlaces(spacesRemaining: number | null | undefined): number {
  if (spacesRemaining != null) {
    return Math.max(0, spacesRemaining);
  }
  return MAX_PLACES_NO_CAPACITY;
}

interface SharedSessionBookingPageProps {
  loggedIn: boolean;
  setLoggedIn: (loggedIn: boolean) => void;
}

const SharedSessionBookingPage: React.FC<SharedSessionBookingPageProps> = ({
  loggedIn,
  setLoggedIn,
}) => {
  const { shareToken } = useParams<{ shareToken: string }>();
  const [info, setInfo] = useState<SharedOneOffSessionInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [booking, setBooking] = useState(false);
  const [paymentDetails, setPaymentDetails] =
    useState<BankTransferPaymentDetails | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadInfo = useCallback(async () => {
    if (!shareToken || !loggedIn) return;
    try {
      setLoading(true);
      setError(null);
      setNotFound(false);
      const data = await fetchSharedOneOffSession(shareToken);
      setInfo(data);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 404) {
        setNotFound(true);
        setInfo(null);
      } else {
        console.error(err);
        setError("Failed to load session. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  }, [loggedIn, shareToken]);

  useEffect(() => {
    loadInfo();
  }, [loadInfo]);

  const handleBook = async (quantity: number) => {
    if (!shareToken) return;
    try {
      setBooking(true);
      setError(null);
      const result = await bookOneOffSession({ share_token: shareToken, quantity });
      setConfirmOpen(false);
      if (result.paid) {
        await loadInfo();
        return;
      }
      setPaymentDetails({
        paymentReference: result.payment_reference,
        amount: result.amount,
      });
      await loadInfo();
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number; data?: string } })
        ?.response?.status;
      const data = (err as { response?: { data?: string } })?.response?.data;
      if (status === 403) {
        setError("You need to be a member of this club to book this session.");
      } else if (status === 409) {
        setError(
          typeof data === "string" && data
            ? data
            : "This session is full.",
        );
        await loadInfo();
      } else if (status === 400 && typeof data === "string") {
        setError(data);
      } else {
        setError("Could not complete booking. Please try again.");
      }
    } finally {
      setBooking(false);
    }
  };

  if (!loggedIn) {
    return (
      <Box minHeight="100vh" bgcolor="#f5f4f0">
        <Container maxWidth="sm" sx={{ pt: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Sign in to book this session. You must be a member of the club.
          </Alert>
        </Container>
        <Login setLoggedIn={setLoggedIn} />
      </Box>
    );
  }

  if (!shareToken) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Invalid booking link.</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Box minHeight="40vh" display="flex" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  if (notFound) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">This booking link is no longer available.</Alert>
      </Container>
    );
  }

  if (error && !info) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!info) {
    return null;
  }

  const session = info.session;
  const dateLabel = dayjs(session.session_date).format("DD MMM YYYY, HH:mm");
  const full =
    session.spaces_remaining != null && session.spaces_remaining <= 0;
  const placesHeld = info.my_places_count ?? 0;
  const invoiceIds =
    info.my_invoice_ids?.length
      ? info.my_invoice_ids
      : info.my_invoice_id != null
        ? [info.my_invoice_id]
        : [];

  return (
    <Box minHeight="100vh" bgcolor="#f5f4f0" py={3}>
      <Container maxWidth="sm">
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {info.club_name}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Book a place on this session using your club membership.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!info.is_member && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You need to be a member of this club to book this session.
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {session.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {dateLabel}
          </Typography>
          {session.notes?.trim() && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1.5, whiteSpace: "pre-wrap" }}
            >
              {session.notes.trim()}
            </Typography>
          )}
          <Typography variant="body1" sx={{ mt: 2, fontWeight: 600 }}>
            £{(session.fee ?? 0).toFixed(2)} per place
          </Typography>
          {session.capacity != null && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {session.spaces_remaining ?? 0} of {session.capacity} places left
            </Typography>
          )}

          {placesHeld > 0 && (
            <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
              <Chip
                color="success"
                label={
                  placesHeld === 1
                    ? "1 place booked"
                    : `${placesHeld} places booked`
                }
              />
              {invoiceIds.map((id) => (
                <Chip
                  key={id}
                  label={buildCompactInvoiceReference([id])}
                  variant="outlined"
                />
              ))}
            </Stack>
          )}

          {!full ? (
            <Button
              variant="contained"
              fullWidth
              sx={{ mt: 3, textTransform: "none", fontWeight: 600 }}
              disabled={!info.is_member || booking}
              onClick={() => setConfirmOpen(true)}
            >
              {booking
                ? "Booking…"
                : placesHeld > 0
                  ? "Book more places"
                  : "Book my place"}
            </Button>
          ) : placesHeld === 0 ? (
            <Button fullWidth disabled sx={{ mt: 3, textTransform: "none" }}>
              Session full
            </Button>
          ) : null}
        </Paper>
      </Container>

      <ConfirmSessionBookingDialog
        open={confirmOpen}
        details={{
          name: session.name,
          dateLabel,
          fee: session.fee ?? 0,
          notes: session.notes,
          maxPlaces: maxBookablePlaces(session.spaces_remaining),
        }}
        confirming={booking}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleBook}
      />

      <BankTransferPaymentDialog
        open={paymentDetails != null}
        details={paymentDetails}
        onClose={() => setPaymentDetails(null)}
      />
    </Box>
  );
};

export default SharedSessionBookingPage;
